import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SkeletonLoader from 'Douleutaras/components/Loaders/SkeletonLoader';
import useIsHydrated from 'Douleutaras/hooks/useIsHydrated';

import ServiceCategoryRow from './ServiceCategoryRow';
import { CatalogueCategory, CatalogueService, ServicesCatalogueData } from './types';
import './ServicesCatalogue.scss';

/** Categories shown before the show-more control clamps a long catalogue. */
const MAX_COLLAPSED_CATEGORIES = 4;
const SKELETON_ROWS = 3;

interface ServicesCatalogueProps {
  catalogue?: ServicesCatalogueData;
  loading?: boolean;
  error?: boolean;
  onServiceClick?(service: CatalogueService): void;
  onCategoryExpand?(category: CatalogueCategory): void;
  onCategoryInfoClick?(category: CatalogueCategory): void;
  onServiceInfoClick?(service: CatalogueService): void;
}

/** True when the catalogue has at least one category to render (drives section and tab visibility). */
export const hasRenderableCatalogue = (catalogue?: ServicesCatalogueData): boolean =>
  Boolean(catalogue?.categories.length);

const hasPricedService = (services: Array<CatalogueService>): boolean =>
  services.some(service => Boolean(service.price) || hasPricedService(service.children ?? []));

/**
 * The `has_services` / `has_service_prices` flags for `Pro Profile Viewed` (#5617), derived from
 * the catalogue this page already fetches while #5614 has yet to put them on the profile payload.
 *
 * `hasServicePrices` counts any published price, deliberately not only the displayable ones: the
 * flag means the pro published a price, which stays true for a row the UI happens not to render.
 */
export const deriveServicePriceFlags = (
  catalogue?: ServicesCatalogueData
): { hasServices: boolean; hasServicePrices: boolean } => {
  const categories = catalogue?.categories ?? [];

  return {
    hasServices: categories.some(category => category.services.length > 0),
    hasServicePrices: categories.some(category => hasPricedService(category.services))
  };
};

/**
 * Section wrapper for the pro-profile services catalogue (ticket #5593, `01`).
 * Handles loading (skeleton), error (placeholder) and empty (renders nothing) states,
 * maps categories, and clamps long catalogues behind a show-more control.
 */
export const ServicesCatalogue = ({
  catalogue,
  loading = false,
  error = false,
  onServiceClick,
  onCategoryExpand,
  onCategoryInfoClick,
  onServiceInfoClick
}: ServicesCatalogueProps) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const hydrated = useIsHydrated();

  if (loading) {
    const skeletonRows = Array.from({ length: SKELETON_ROWS });

    return (
      <div styleName="services-catalogue services-catalogue--loading">
        {skeletonRows.map((_, index) => (
          <div key={index} styleName="services-catalogue__skeleton">
            <SkeletonLoader type="line" height={52} borderRadius={12} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div styleName="services-catalogue services-catalogue--error">
        {t('T.PROS-PROFILE.SERVICES.ERROR')}
      </div>
    );
  }

  if (!catalogue || !hasRenderableCatalogue(catalogue)) {
    // No priced services → no section (the tab is hidden by ProProfile).
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  const { categories } = catalogue;
  const isClamped = categories.length > MAX_COLLAPSED_CATEGORIES;
  // Render every category on the server (and the matching first client render) so all prices
  // are in the crawlable HTML; apply the show-more clamp only after hydration.
  const visibleCategories = hydrated && isClamped && !showAll
    ? categories.slice(0, MAX_COLLAPSED_CATEGORIES)
    : categories;

  return (
    <div styleName="services-catalogue">
      <ul styleName="services-catalogue__list">
        {visibleCategories.map(category => (
          <ServiceCategoryRow
            key={category.categoryId}
            category={category}
            onServiceClick={onServiceClick}
            onCategoryExpand={onCategoryExpand}
            onCategoryInfoClick={onCategoryInfoClick}
            onServiceInfoClick={onServiceInfoClick}
          />
        ))}
      </ul>

      {hydrated && isClamped && (
        <button
          type="button"
          styleName="services-catalogue__show-more"
          onClick={() => setShowAll(value => !value)}
        >
          {showAll
            ? t('T.PROS-PROFILE.SERVICES.SHOW-LESS')
            : t('T.PROS-PROFILE.SERVICES.SHOW-MORE')}
        </button>
      )}
    </div>
  );
};

export default ServicesCatalogue;
